import { nomeEmpresa, sociosPf, type CnpjConsulta } from "@/src/lib/cnpj";

export type ChatStep =
  | "tipo"
  | "cnpj"
  | "confirmar_empresa"
  | "empresa"
  | "faturamento"
  | "restricoes"
  | "endividamento"
  | "socio"
  | "nome"
  | "telefone"
  | "email"
  | "cpf"
  | "nascimento"
  | "conjuge"
  | "consentimento"
  | "produto"
  | "valor"
  | "cidade"
  | "done";

export type ChatReply = {
  label: string;
  value: string;
  hint?: string;
};

export type Qualification = {
  origem?: string;
  intencao?: string;
  tipo?: string;
  cnpj?: string;
  empresa?: string;
  empresaSugerida?: string;
  situacaoCadastral?: string;
  sociosNomes?: string[];
  faturamento?: string;
  restricoes?: string;
  endividamento?: string;
  nome?: string;
  telefone?: string;
  email?: string;
  cpf?: string;
  nascimento?: string;
  conjuge?: string;
  produto?: string;
  valor?: string;
  cidade?: string;
};

export type ScriptTurn = {
  step: ChatStep;
  qualification: Qualification;
  botMessages: string[];
  replies: ChatReply[];
  acceptText: boolean;
  placeholder?: string;
};

const TIPO_REPLIES: ChatReply[] = [
  { label: "Empresa", value: "empresa", hint: "CNPJ e dados da empresa" },
  { label: "Pessoa física", value: "pessoa", hint: "CPF e dados pessoais" },
];

const RESTRICAO_REPLIES: ChatReply[] = [
  { label: "Sim", value: "sim" },
  { label: "Não", value: "nao" },
  { label: "Não sei informar", value: "nao_sei" },
];

const CONJUGE_REPLIES: ChatReply[] = [
  { label: "Sim", value: "sim" },
  { label: "Não", value: "nao" },
];

const PULAR_NASCIMENTO: ChatReply[] = [{ label: "Prefiro não informar", value: "pular" }];

const CONSENT_REPLIES: ChatReply[] = [{ label: "Aceito os termos", value: "aceito" }];

const CONFIRM_EMPRESA: ChatReply[] = [
  { label: "Confirmar", value: "confirmar" },
  { label: "Alterar nome", value: "alterar" },
];

const PRODUTO_REPLIES: ChatReply[] = [
  { label: "Capital de giro", value: "capital-giro" },
  { label: "Antecipação de recebíveis", value: "antecipacao" },
  { label: "Crédito imobiliário", value: "imobiliario" },
  { label: "Home equity", value: "home-equity" },
  { label: "Financiamento de veículo", value: "veiculo" },
  { label: "FINAME", value: "finame" },
  { label: "Ainda não sei", value: "indefinido" },
];

const VALOR_REPLIES: ChatReply[] = [
  { label: "Até R$ 100 mil", value: "ate-100" },
  { label: "R$ 100 mil a R$ 500 mil", value: "100-500" },
  { label: "R$ 500 mil a R$ 2 milhões", value: "500-2000" },
  { label: "Acima de R$ 2 milhões", value: "acima-2000" },
  { label: "Ainda não sei", value: "indefinido" },
];

const ORIGIN_LABELS: Record<string, string> = {
  "auto-pecas": "Landing Autopeças",
  transportadoras: "Landing Transportadoras",
  clinicas: "Landing Clínicas médicas",
  odontologia: "Landing Consultórios odontológicos",
  home: "Página inicial",
};

const ORIGIN_GREETING: Record<string, string> = {
  "auto-pecas": "Vi que você veio da página de Autopeças.",
  transportadoras: "Vi que você veio da página de Transportadoras.",
  clinicas: "Vi que você veio da página de Clínicas médicas.",
  odontologia: "Vi que você veio da página de Consultórios odontológicos.",
  home: "Vi que você veio da página inicial.",
};

const TIPO_LABELS: Record<string, string> = {
  empresa: "Empresa",
  pessoa: "Pessoa física",
};

const RESTRICAO_LABELS: Record<string, string> = {
  sim: "Sim",
  nao: "Não",
  nao_sei: "Não sei informar",
};

const TERMS = [
  "Para seguir, preciso da sua autorização. A pré-análise usa os dados para contato e para olhar soluções compatíveis. Ela não garante aprovação de crédito.",
  "O aceite cobre o tratamento dos dados (LGPD), consulta ao SCR do Banco Central, consulta a bureaus de crédito como a Serasa, uso de dados fiscais e o compartilhamento só com quem participa da análise.",
];

export function originLabel(origem: string | undefined) {
  if (!origem) return undefined;
  return ORIGIN_LABELS[origem] ?? origem;
}

function matchReply(replies: ChatReply[], raw: string) {
  const normalized = raw.trim().toLowerCase();
  return replies.find(
    (reply) => reply.value === normalized || reply.label.toLowerCase() === normalized,
  );
}

function isEmpresa(qualification: Qualification) {
  return qualification.tipo === "empresa";
}

function turn(
  step: ChatStep,
  qualification: Qualification,
  botMessages: string[],
  replies: ChatReply[] = [],
  placeholder?: string,
  acceptText = true,
): ScriptTurn {
  return { step, qualification, botMessages, replies, acceptText, placeholder };
}

function stay(step: ChatStep, qualification: Qualification, message: string): ScriptTurn {
  return turn(step, qualification, [message], repliesFor(step, qualification), placeholderFor(step, qualification));
}

export function startTurn(origem?: string | null): ScriptTurn {
  const cameFrom = origem ? (ORIGIN_GREETING[origem] ?? `Vi que você veio de ${origem}.`) : null;
  const intro = cameFrom
    ? `Olá, eu sou o atendimento da OBANKER. ${cameFrom}`
    : "Olá, eu sou o atendimento da OBANKER.";

  return turn(
    "tipo",
    { origem: origem || undefined, intencao: "credito" },
    [
      intro,
      "Antes de falar da linha de crédito, preciso dos dados da pré-análise. Nesta conversa nada é enviado.",
      "O cadastro é de empresa ou de pessoa física?",
    ],
    TIPO_REPLIES,
  );
}

export function advanceTurn(step: ChatStep, qualification: Qualification, raw: string): ScriptTurn {
  const value = raw.trim();
  if (!value) return stay(step, qualification, "Pode repetir? Preciso dessa informação para seguir.");

  if (step === "tipo") {
    const matched = matchReply(TIPO_REPLIES, value);
    if (!matched) return stay(step, qualification, "Escolha empresa ou pessoa física para eu abrir o cadastro certo.");
    const next = { ...qualification, tipo: matched.value };
    if (matched.value === "empresa") {
      return turn(
        "cnpj",
        next,
        ["Qual o CNPJ da empresa? Pode digitar só os números que eu busco os dados para você confirmar."],
        [],
        "00.000.000/0000-00",
      );
    }
    return turn(
      "nome",
      next,
      ["Qual o seu nome completo?"],
      [],
      "Nome completo",
    );
  }

  if (step === "cnpj") {
    const digits = value.replace(/\D/g, "");
    if (digits.length !== 14) return stay(step, qualification, "O CNPJ precisa ter 14 números.");
    return turn(
      "empresa",
      { ...qualification, cnpj: formatCnpj(digits) },
      ["Qual a razão social ou o nome fantasia?"],
      [],
      "Nome da empresa",
    );
  }

  if (step === "confirmar_empresa") {
    const matched = matchReply(CONFIRM_EMPRESA, value);
    if (matched?.value === "alterar") {
      return turn("empresa", qualification, ["Qual nome devo usar?"], [], "Nome da empresa");
    }
    if (matched?.value === "confirmar") {
      return askFaturamento({
        ...qualification,
        empresa: titleName(qualification.empresaSugerida ?? ""),
      });
    }
    return askFaturamento({ ...qualification, empresa: value });
  }

  if (step === "empresa") {
    return turn(
      "faturamento",
      { ...qualification, empresa: value },
      ["Qual o faturamento fiscal declarado nos últimos 12 meses?"],
      [],
      "R$ 0,00",
    );
  }

  if (step === "faturamento") {
    const next = { ...qualification, faturamento: formatMoney(value) };
    if (isEmpresa(qualification)) {
      return turn(
        "restricoes",
        next,
        ["A empresa possui restrições?"],
        RESTRICAO_REPLIES,
      );
    }
    return askPhone(next);
  }

  if (step === "restricoes") {
    const matched = matchReply(RESTRICAO_REPLIES, value);
    if (!matched) return stay(step, qualification, "Responda sim, não ou não sei informar.");
    const next = { ...qualification, restricoes: matched.value };
    if (matched.value === "sim") {
      return turn(
        "endividamento",
        next,
        ["Qual o endividamento bancário atual da empresa?"],
        [],
        "R$ 0,00",
      );
    }
    return askRepresentative(next, "Qual o nome completo do representante legal?");
  }

  if (step === "endividamento") {
    return askRepresentative(
      { ...qualification, endividamento: formatMoney(value) },
      "Qual o nome completo do representante legal?",
    );
  }

  if (step === "socio") {
    if (value.toLowerCase() === "outro" || value.toLowerCase() === "outro nome") {
      return askName(qualification, "Qual o nome completo do representante legal?");
    }
    return askPhone({ ...qualification, nome: titleName(value) });
  }

  if (step === "nome") {
    const next = { ...qualification, nome: value };
    if (isEmpresa(qualification)) return askPhone(next);
    return turn(
      "faturamento",
      next,
      ["Qual a renda mensal estimada?"],
      [],
      "R$ 0,00",
    );
  }

  if (step === "telefone") {
    const digits = value.replace(/\D/g, "");
    if (digits.length !== 10 && digits.length !== 11) {
      return stay(step, qualification, "Informe um celular com DDD. Exemplo: (11) 99999-9999.");
    }
    return turn(
      "email",
      { ...qualification, telefone: formatPhone(digits) },
      ["Qual o e-mail?"],
      [],
      "seu@email.com",
    );
  }

  if (step === "email") {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return stay(step, qualification, "Esse e-mail não parece válido. Pode conferir?");
    }
    return turn(
      "cpf",
      { ...qualification, email: value },
      [isEmpresa(qualification) ? "Confirme o CPF do representante." : "Qual o seu CPF?"],
      [],
      "000.000.000-00",
    );
  }

  if (step === "cpf") {
    const digits = value.replace(/\D/g, "");
    if (digits.length !== 11) return stay(step, qualification, "O CPF precisa ter 11 números.");
    const next = { ...qualification, cpf: formatCpf(digits) };
    const optional = isEmpresa(qualification);
    return turn(
      "nascimento",
      next,
      [
        optional
          ? "Qual a data de nascimento do representante? Se preferir, pode pular."
          : "Qual a sua data de nascimento?",
      ],
      optional ? PULAR_NASCIMENTO : [],
      "DD/MM/AAAA",
    );
  }

  if (step === "nascimento") {
    const skipped = isEmpresa(qualification) && matchReply(PULAR_NASCIMENTO, value);
    const parsed = skipped ? undefined : parseDate(value);
    if (!skipped && !parsed) {
      return stay(step, qualification, "Use o formato DD/MM/AAAA. Exemplo: 15/03/1985.");
    }
    return turn(
      "conjuge",
      { ...qualification, nascimento: parsed },
      [isEmpresa(qualification) ? "O representante possui cônjuge?" : "Você possui cônjuge?"],
      CONJUGE_REPLIES,
    );
  }

  if (step === "conjuge") {
    const matched = matchReply(CONJUGE_REPLIES, value);
    if (!matched) return stay(step, qualification, "Responda sim ou não.");
    return turn("consentimento", { ...qualification, conjuge: matched.value }, TERMS, CONSENT_REPLIES, undefined, true);
  }

  if (step === "consentimento") {
    const matched = matchReply(CONSENT_REPLIES, value);
    if (!matched) {
      return stay(step, qualification, "Para continuar a pré-análise, é preciso aceitar os termos.");
    }
    return turn(
      "produto",
      qualification,
      ["Dados anotados. Qual linha de crédito faz mais sentido agora?"],
      PRODUTO_REPLIES,
    );
  }

  if (step === "produto") {
    const matched = matchReply(PRODUTO_REPLIES, value);
    return turn(
      "valor",
      { ...qualification, produto: matched?.label ?? value },
      ["Qual faixa de valor você tem em mente?"],
      VALOR_REPLIES,
    );
  }

  if (step === "valor") {
    const matched = matchReply(VALOR_REPLIES, value);
    return turn(
      "cidade",
      { ...qualification, valor: matched?.label ?? value },
      ["Em qual cidade está a operação?"],
      [],
      "Cidade",
    );
  }

  if (step === "cidade") {
    const next = { ...qualification, cidade: value };
    return turn(
      "done",
      next,
      [
        `Obrigado, ${next.nome}. A pré-análise ficou anotada nesta conversa.`,
        "Ainda não vira um lead no painel: este fluxo é um protótipo e nada foi salvo.",
      ],
      [{ label: "Nova conversa", value: "restart" }],
      undefined,
      false,
    );
  }

  return startTurn(qualification.origem);
}

export function afterCnpjLookup(qualification: Qualification, cnpjDigits: string, payload: CnpjConsulta): ScriptTurn {
  const socios = [...new Set(sociosPf(payload.QSA).map((row) => row.nome_socio?.trim()).filter((nome): nome is string => Boolean(nome)))];
  const nome = nomeEmpresa(payload);
  const next: Qualification = {
    ...qualification,
    cnpj: formatCnpj(cnpjDigits),
    empresaSugerida: nome ? titleName(nome) : undefined,
    situacaoCadastral: payload.situacao_cadastral?.trim() || undefined,
    sociosNomes: socios.length ? socios : undefined,
  };

  if (payload.consulta_manual || !nome) {
    return turn(
      "empresa",
      next,
      [
        payload.message ?? "Não consegui consultar esse CNPJ agora.",
        "Qual a razão social ou o nome fantasia?",
      ],
      [],
      "Nome da empresa",
    );
  }

  const situacao = next.situacaoCadastral;
  const alerta = situacao && situacao.toLowerCase() !== "ativa" ? `A situação cadastral está como ${situacao}.` : null;
  const razao = payload.razao_social?.trim();
  const fantasia = payload.nome_fantasia?.trim();
  const detalhe =
    fantasia && razao && fantasia.toLowerCase() !== razao.toLowerCase() ? `Razão social: ${titleName(razao)}.` : null;

  return turn(
    "confirmar_empresa",
    next,
    [
      `Encontrei ${next.empresaSugerida}.`,
      ...(alerta ? [alerta] : []),
      ...(detalhe ? [detalhe] : []),
      "Confirma esse nome ou prefere alterar?",
    ],
    CONFIRM_EMPRESA,
  );
}

export function qualificationLines(qualification: Qualification) {
  const rows: { label: string; value: string }[] = [];
  const push = (label: string, value: string | undefined) => {
    if (value) rows.push({ label, value });
  };

  push("Origem", originLabel(qualification.origem));
  rows.push({ label: "Intenção", value: "Crédito" });
  push("Perfil", qualification.tipo ? (TIPO_LABELS[qualification.tipo] ?? qualification.tipo) : undefined);
  push("CNPJ", qualification.cnpj);
  push("Empresa", qualification.empresa);
  push("Situação cadastral", qualification.situacaoCadastral);
  push(isEmpresa(qualification) ? "Faturamento 12 meses" : "Renda mensal", qualification.faturamento);
  push("Restrições", qualification.restricoes ? RESTRICAO_LABELS[qualification.restricoes] : undefined);
  push("Endividamento", qualification.endividamento);
  push("Nome", qualification.nome);
  push("Celular", qualification.telefone);
  push("E-mail", qualification.email);
  push("CPF", qualification.cpf);
  push("Nascimento", qualification.nascimento);
  push("Cônjuge", qualification.conjuge ? (qualification.conjuge === "sim" ? "Sim" : "Não") : undefined);
  push("Produto", qualification.produto);
  push("Valor", qualification.valor);
  push("Cidade", qualification.cidade);

  return rows;
}

function askName(qualification: Qualification, message: string): ScriptTurn {
  return turn("nome", qualification, [message], [], "Nome completo");
}

function askFaturamento(qualification: Qualification): ScriptTurn {
  return turn(
    "faturamento",
    qualification,
    ["Qual o faturamento fiscal declarado nos últimos 12 meses?"],
    [],
    "R$ 0,00",
  );
}

function askRepresentative(qualification: Qualification, message: string): ScriptTurn {
  const socios = (qualification.sociosNomes ?? []).slice(0, 8);
  if (!socios.length) return askName(qualification, message);
  return turn(
    "socio",
    qualification,
    [message, "Encontrei estes sócios no CNPJ. Escolha o seu ou informe outro nome."],
    socioReplies(socios),
    "Nome completo",
  );
}

function socioReplies(socios: string[]): ChatReply[] {
  return [...socios.map((nome) => ({ label: titleName(nome), value: nome })), { label: "Outro nome", value: "outro" }];
}

function askPhone(qualification: Qualification): ScriptTurn {
  return turn("telefone", qualification, ["Qual o celular, com DDD?"], [], "(00) 00000-0000");
}

function repliesFor(step: ChatStep, qualification: Qualification): ChatReply[] {
  if (step === "tipo") return TIPO_REPLIES;
  if (step === "confirmar_empresa") return CONFIRM_EMPRESA;
  if (step === "socio") return socioReplies(qualification.sociosNomes ?? []);
  if (step === "restricoes") return RESTRICAO_REPLIES;
  if (step === "nascimento" && isEmpresa(qualification)) return PULAR_NASCIMENTO;
  if (step === "conjuge") return CONJUGE_REPLIES;
  if (step === "consentimento") return CONSENT_REPLIES;
  if (step === "produto") return PRODUTO_REPLIES;
  if (step === "valor") return VALOR_REPLIES;
  if (step === "done") return [{ label: "Nova conversa", value: "restart" }];
  return [];
}

export function maskChatInput(step: ChatStep, raw: string) {
  if (step === "cnpj") return maskDigits(raw, 14, [2, 5, 8, 12], [".", ".", "/", "-"]);
  if (step === "cpf") return maskDigits(raw, 11, [3, 6, 9], [".", ".", "-"]);
  if (step === "telefone") return maskPhone(raw);
  if (step === "nascimento") return maskDigits(raw, 8, [2, 4], ["/", "/"]);
  if (step === "faturamento" || step === "endividamento") return maskMoney(raw);
  return raw;
}

export function chatInputMode(step: ChatStep): "text" | "numeric" | "decimal" | "email" | "tel" {
  if (step === "cnpj" || step === "cpf" || step === "nascimento") return "numeric";
  if (step === "telefone") return "tel";
  if (step === "email") return "email";
  if (step === "faturamento" || step === "endividamento") return "decimal";
  return "text";
}

function maskDigits(raw: string, max: number, breaks: number[], separators: string[]) {
  const digits = raw.replace(/\D/g, "").slice(0, max);
  let out = "";
  for (let index = 0; index < digits.length; index += 1) {
    const breakAt = breaks.indexOf(index);
    if (breakAt >= 0) out += separators[breakAt];
    out += digits[index];
  }
  return out;
}

function maskPhone(raw: string) {
  const digits = raw.replace(/\D/g, "").slice(0, 11);
  if (!digits) return "";
  if (digits.length < 3) return `(${digits}`;
  const mobile = digits.length > 10;
  const split = mobile ? 7 : 6;
  const head = `(${digits.slice(0, 2)}) ${digits.slice(2, Math.min(digits.length, split))}`;
  if (digits.length <= split) return head;
  return `${head}-${digits.slice(split)}`;
}

function maskMoney(raw: string) {
  const cleaned = raw.replace(/[^\d,]/g, "");
  if (!cleaned) return "";
  const comma = cleaned.indexOf(",");
  const intRaw = (comma === -1 ? cleaned : cleaned.slice(0, comma)).replace(/\D/g, "");
  const cents = comma === -1 ? "" : cleaned.slice(comma + 1).replace(/\D/g, "").slice(0, 2);
  const intDigits = intRaw.replace(/^0+(?=\d)/, "");
  const grouped = (intDigits || (comma === -1 ? "" : "0")).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const body = comma === -1 ? grouped : `${grouped},${cents}`;
  return body ? `R$ ${body}` : "";
}

function placeholderFor(step: ChatStep, qualification: Qualification) {
  if (step === "cnpj") return "00.000.000/0000-00";
  if (step === "confirmar_empresa") return "Ou digite outro nome";
  if (step === "empresa") return "Nome da empresa";
  if (step === "socio") return "Nome completo";
  if (step === "faturamento" || step === "endividamento") return "R$ 0,00";
  if (step === "nome") return "Nome completo";
  if (step === "telefone") return "(00) 00000-0000";
  if (step === "email") return "seu@email.com";
  if (step === "cpf") return "000.000.000-00";
  if (step === "nascimento") return isEmpresa(qualification) ? "DD/MM/AAAA" : "DD/MM/AAAA";
  if (step === "cidade") return "Cidade";
  return undefined;
}

function titleName(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  const letters = trimmed.replace(/[^A-Za-zÀ-ÿ]/g, "");
  const upper = letters && letters === letters.toUpperCase();
  if (!upper) return trimmed;
  return trimmed
    .toLocaleLowerCase("pt-BR")
    .replace(/(^|[\s'-])(\p{L})/gu, (_, sep: string, letter: string) => sep + letter.toLocaleUpperCase("pt-BR"));
}

function formatCnpj(digits: string) {
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

function formatCpf(digits: string) {
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function formatPhone(digits: string) {
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
}

function formatMoney(raw: string) {
  const trimmed = raw.trim();
  const normalized = trimmed.includes(",")
    ? trimmed.replace(/[^\d,]/g, "").replace(",", ".")
    : trimmed.replace(/\D/g, "");
  const amount = Number(normalized);
  if (!normalized || !Number.isFinite(amount)) return trimmed;
  return amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function parseDate(raw: string) {
  const match = raw.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return undefined;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  const valid =
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    year >= 1900 &&
    date.getTime() <= Date.now();
  if (!valid) return undefined;
  return `${match[1]}/${match[2]}/${match[3]}`;
}
